import {FC, ReactElement} from "react";
import {TreeItem2, SimpleTreeView, treeItemClasses} from "@mui/x-tree-view";
import {ChevronDownIcon, ChevronRightIcon} from "@interstate/components/Icons";
import {useInterstateToken} from "@interstate/components/InterstateThemeProvider";
import {CreateResultNode} from "../../view-model";
import {collectAllCaIds} from "../../helpers";
import {usePlan} from "../../context";
import {CreateResultNodeLabelType} from "./CreateResultNodeLabelType";
import "./CreateResultTreeView.scss";

export interface CreateResultTreeViewProps {
  result: CreateResultNode;
  label: CreateResultNodeLabelType;
}

export const CreateResultTreeView: FC<CreateResultTreeViewProps> = ({
  result,
  label: TreeItemLabel
}) => {
  const token = useInterstateToken();
  const {from} = usePlan();

  const toTreeItem = (node: CreateResultNode): ReactElement => {
    const prototype = node.caId === from.caId;
    const children = node.children ? [...node.children] : [];
    return (
      <TreeItem2
        key={node.caId}
        itemId={node.caId}
        data-testid={`create-result-node-${node.caId}`}
        className={`create-tree-node create-result-node${prototype ? " create-result-node--from" : ""}`}
        label={<TreeItemLabel node={node} />}>
        {children
          .sort((a, b) => {
            // Always render the from node first
            if (a.caId === from.caId) return -1;
            if (b.caId === from.caId) return 1;
            return a.dbaName.localeCompare(b.dbaName);
          })
          .map(toTreeItem)}
      </TreeItem2>
    );
  };

  return (
    <SimpleTreeView
      id={`create-result-tree-view-${result.caId}`}
      data-testid={`create-result-tree-view-${result.caId}`}
      className={"create-tree-view create-result-tree-view"}
      sx={{
        [`& .${treeItemClasses.groupTransition}`]: {
          marginLeft: 1.9,
          borderLeft: theme => `1px solid ${theme.palette.divider}`
        }
      }}
      expandedItems={collectAllCaIds(result)}
      disableSelection={true}
      disabledItemsFocusable={true}
      selectedItems={from.caId}
      slots={{
        expandIcon: ChevronRightIcon,
        collapseIcon: ChevronDownIcon
      }}
      slotProps={{
        collapseIcon: {
          color: token("sem.color.on-surface.primary")
        },
        expandIcon: {
          color: token("sem.color.on-surface.primary")
        }
      }}>
      {toTreeItem(result)}
    </SimpleTreeView>
  );
};
